<template>
  <app-page-wrapper-grid>
    <div class="iw-layout-grid__row iw-grid__row__16">
      <!-- Back button -->
      <div class="iw-layout-grid__col--span-12">
        <back-button />
      </div>
      <!-- Form -->
      <div class="iw-layout-grid__col--span-4 iw-layout-grid__col--offset-4-desktop">
        <div class="login">
          <form id="authForm" class="login__form">
            <h3 class="login__title iw-page-title">Login to your account</h3>
            <template v-if="(isFormSubmitted && v$.$invalid) || responseWithErrors">
              <app-notifier
                v-for="(message, index) in errors"
                :key="index"
                class="checkout__error"
                :type="'failed'"
                :data="message"
              />
            </template>
            <app-input
              ref="email"
              v-model="v$.email.$model"
              placeholder=""
              label="Email"
              :has-error="v$.email.$error && isFormSubmitted"
              class="iw-body-text-small login__input"
              :error-message="
                v$.email.$error && isFormSubmitted && !responseWithErrors ? validatorsCollection.get('email').errorMessage : ''
              "
            />
            <app-input
              ref="password"
              v-model="v$.password.$model"
              type="password"
              label="Password"
              :has-error="v$.password.$error && isFormSubmitted"
              class="iw-body-text-small login__input"
              placeholder=""
              :error-message="
                v$.password.$error && isFormSubmitted && !responseWithErrors
                  ? validatorsCollection.get('password').errorMessage
                  : ''
              "
            />
            <app-button text="Login" :aria-label="'Login'" class="login__button__login" :handler="submit" />
            <div class="login__section" :class="!continueAsGuest && 'login__link__forgotpassword'">
              <div v-if="continueAsGuest" class="login__container">
                <div>
                  New User?
                  <app-link
                    :route-to="{
                      name: ROUTE_NAMES.REGISTRATION,
                      params: { target: $route.params.target || null }
                    }"
                    class="login__container__link m-l-10"
                    >Register</app-link
                  >
                </div>
                <div>
                  <app-link :route-to="{ name: ROUTE_NAMES.FORGOT_PASSWORD }" class="login__container__link"
                    >Forgot Password?</app-link
                  >
                </div>
              </div>
              <app-link v-else :route-to="{ name: ROUTE_NAMES.FORGOT_PASSWORD }" class="login__container__link"
                >Forgot Password?</app-link
              >
            </div>
            <div class="login__separator">
              <hr class="login__separator__line" />
              <h4 class="iw-body-text-small login__separator__text" :style="continueAsGuest ? '' : 'min-width: 210px;'">
                {{ continueAsGuest ? "OR" : "Don't Have an Account?" }}
              </h4>
              <hr class="login__separator__line" />
            </div>
            <app-button
              :text="continueAsGuest ? 'Continue As Guest' : 'Register'"
              :aria-label="continueAsGuest ? 'Continue As Guest' : 'Register'"
              outlined
              class="login__button"
              :handler="routeToNext"
            />
          </form>
        </div>
      </div>
    </div>
  </app-page-wrapper-grid>
</template>

<script>
import { mapActions } from "vuex";
import useVuelidate from "@vuelidate/core";
import { emailValidation, loginPasswordValidations, ROUTE_NAMES, REQUIRED_ERR_MSG } from "@/utils";
import AppPageWrapperGrid from "../../components/base/app-page-wrapper-grid/AppPageWrapperGrid.vue";
import BackButton from "../../components/buttons/back-button/BackButton";

export default {
  name: "Login",
  components: {
    AppPageWrapperGrid,
    BackButton
  },
  props: {
    continueAsGuest: {
      type: String
    }
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      email: "",
      password: "",
      isFormSubmitted: false,
      responseWithErrors: false,
      errors: [],
      validatorsCollection: new Map(),
      remember: [],
      ROUTE_NAMES
    };
  },
  computed: {
    rememberUser: function () {
      return this.remember.length > 0;
    }
  },
  validations: {
    email: emailValidation(),
    password: loginPasswordValidations()
  },
  methods: {
    ...mapActions("user", ["authorization"]),
    submit() {
      this.isFormSubmitted = false;
      this.responseWithErrors = false;
      this.errors.length = 0;

      this.v$.$touch();
      this.checkInvalidFields();
      this.isFormSubmitted = true;

      if (!this.v$.$invalid) {
        return this.authorization({
          email: this.email,
          password: this.password,
          remember: this.rememberUser
        }).then(
          () => {
            if ("target" in this.$route.params) {
              this.$router.push({ name: this.$route.params.target, params: this.$route.params });
            } else {
              this.$router.push({ name: ROUTE_NAMES.USER_INFO });
            }
          },
          (error) => {
            this.errors.length = 0;
            if (error === 408) {
              this.errors.push("Your account cannot be reached at this time. Please try again.");
            } else if (error === 404) {
              this.errors.push("That email address and password combination is invalid. Please try again.");
            } else {
              if (error && (error.status === 401 || error.status === 404)) {
                this.errors.push("Invalid login. Please enter a valid email address and password combination");
              } else {
                this.errors.push("Login failed. Please try again later.");
              }
            }
            this.responseWithErrors = true;
          }
        );
      }

      return true;
    },
    routeToNext() {
      const pathName = this.continueAsGuest ? ROUTE_NAMES.CONTINUE_AS_GUEST : ROUTE_NAMES.REGISTRATION;
      this.$router.push({
        name: pathName,
        params: {
          target: this.$route.params.target || null,
          proceedToCheckout: this.$route.params.proceedToCheckout || null
        }
      });
    },
    checkInvalidFields() {
      this.validatorsCollection.forEach((item) => {
        if (item.validator.$invalid) {
          if (item.validator.required.$invalid) {
            if (!this.errors.includes(REQUIRED_ERR_MSG)) {
              this.errors.push(REQUIRED_ERR_MSG);
            }
          }
        }
      });
    }
  },
  mounted() {
    this.validatorsCollection
      .set("email", {
        validator: this.v$.email,
        errorMessage: "Please enter a valid email address"
      })
      .set("password", {
        validator: this.v$.password,
        errorMessage: "Invalid login. Please enter a valid email address and password combination"
      });
    this.$refs.email.$el.focus();
  }
};
</script>

<style lang="scss">
.error {
  text-transform: uppercase;
  color: $error-color;
  margin: 0 0 10px 0;
}

.login {
  display: flex;
  justify-content: center;
  width: 100%;

  &__security-message {
    border-radius: 10px;
    line-height: 1.8em;
    border: solid 2px #1874e0;
    background-color: #eef7ff;
    padding: 10px 10px 0px 10px;
    margin-bottom: 1.5em;

    p {
      margin-top: 0;
    }
  }

  &__title {
    width: 100%;
    text-align: left;
    margin-bottom: 29px;
    @include respond-to-mobile {
      margin-bottom: 19px;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 316px;
    margin-top: 16px;

    @include respond-to-mobile {
      width: 100%;
    }
  }

  &__section {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 27px;

    @include respond-to-mobile {
      margin-top: 30px;
    }
  }

  &__separator {
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: 32.8px;

    @include respond-to-mobile {
      margin-top: 32px;
    }

    &__line {
      flex: 1 30%;
      height: 0.9px;
      background-color: $group-3-color-3;
      opacity: 0.3;
      border: 0px;
    }

    &__text {
      flex: 2;
      text-align: center;
      color: $group-3-color-2;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
    }
  }

  &__input {
    margin-top: 6px;

    @include respond-to-mobile {
      margin-top: 5px;
    }
  }

  &__button {
    margin-top: 30.2px;

    @include respond-to-mobile {
      margin-top: 32px;
    }

    &__login {
      margin-top: 33px;
    }
  }

  &__container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 14px;
    line-height: 1.43;
    color: $group-3-color-2;
    height: 20px;
    &__link {
      font-size: 14px;
    }
  }

  &__link__forgotpassword {
    display: flex;
    justify-content: center;
  }

  &__remember {
    text-transform: uppercase;
  }
}
</style>
